import { ApiProblem, isProblemDetails } from "./problemDetails";

declare const idempotencyKeyBrand: unique symbol;

export type IdempotencyKey = string & {
  readonly [idempotencyKeyBrand]: "IdempotencyKey";
};

export interface RequestOptions extends Omit<RequestInit, "body" | "method"> {
  readonly additionalProtectedScopes?: readonly string[];
  readonly protectedScope?: string;
}

export type MutationMethod = "POST" | "PUT" | "PATCH" | "DELETE";

export interface MutationRequestOptions<TBody = unknown> extends RequestOptions {
  readonly method: MutationMethod;
  readonly idempotencyKey: IdempotencyKey;
  readonly body?: TBody;
}

export type FetchImplementation = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export interface ApiClientOptions {
  readonly baseUrl: string | URL;
  readonly fetch?: FetchImplementation;
  readonly isProtectedScopeAvailable?: (protectedScope: string) => boolean;
  readonly bearerToken?: MemoryBearerToken;
  readonly onForbidden?: (protectedScope: string | undefined) => void | Promise<void>;
  readonly onUnauthorized?: () => void | Promise<void>;
  readonly registerProtectedOperation?: (
    protectedScope: string,
    cancel: () => void,
  ) => () => void;
}

export class MemoryBearerToken {
  #token: string | undefined;

  constructor(token?: string) {
    if (token !== undefined) {
      this.set(token);
    }
  }

  get hasToken(): boolean {
    return this.#token !== undefined;
  }

  set(token: string): void {
    if (token.trim().length === 0 || /[\r\n]/.test(token)) {
      throw new TypeError("Bearer token must be non-blank and single-line");
    }
    this.#token = token;
  }

  clear(): void {
    this.#token = undefined;
  }

  apply(headers: Headers): void {
    if (this.#token !== undefined) {
      headers.set("Authorization", `Bearer ${this.#token}`);
    }
  }
}

export function createIdempotencyKey(value: string): IdempotencyKey {
  if (
    value.length < 1 ||
    value.length > 255 ||
    // \x00 is the deliberate lower bound of a "pure ASCII" range check below,
    // not a stray control character left in by accident.
    // eslint-disable-next-line no-control-regex
    !/^[\x00-\x7F]+$/.test(value) ||
    /^\s+$/.test(value)
  ) {
    throw new TypeError("Idempotency-Key must be 1-255 non-blank ASCII characters");
  }
  return value as IdempotencyKey;
}

export class ApiClient {
  readonly #baseUrl: URL;
  readonly #fetch: FetchImplementation;
  readonly #isProtectedScopeAvailable: ((protectedScope: string) => boolean) | undefined;
  readonly #bearerToken: MemoryBearerToken | undefined;
  readonly #onForbidden: ((protectedScope: string | undefined) => void | Promise<void>) | undefined;
  readonly #onUnauthorized: (() => void | Promise<void>) | undefined;
  readonly #registerProtectedOperation: ApiClientOptions["registerProtectedOperation"];

  constructor(options: ApiClientOptions) {
    const baseUrl = options.baseUrl.toString();
    this.#baseUrl = new URL(baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`);
    this.#fetch = options.fetch ?? fetch;
    this.#isProtectedScopeAvailable = options.isProtectedScopeAvailable;
    this.#bearerToken = options.bearerToken;
    this.#onForbidden = options.onForbidden;
    this.#onUnauthorized = options.onUnauthorized;
    this.#registerProtectedOperation = options.registerProtectedOperation;
  }

  async request<TResponse>(
    path: string | URL,
    options: RequestOptions = {},
  ): Promise<TResponse> {
    const { additionalProtectedScopes, protectedScope, ...requestOptions } = options;
    const url = this.#resolve(path);
    const headers = this.#headers(url, requestOptions.headers);
    return await this.#request<TResponse>(url, {
      ...requestOptions,
      method: "GET",
      headers,
    }, protectedScope, additionalProtectedScopes);
  }

  async mutate<TResponse, TBody = unknown>(
    path: string | URL,
    options: MutationRequestOptions<TBody>,
  ): Promise<TResponse> {
    const {
      additionalProtectedScopes,
      body,
      idempotencyKey,
      method,
      protectedScope,
      ...requestOptions
    } = options;
    const url = this.#resolve(path);
    const headers = this.#headers(url, requestOptions.headers);
    headers.set("Idempotency-Key", idempotencyKey);

    if (body === undefined) {
      return await this.#request<TResponse>(
        url,
        { ...requestOptions, method, headers },
        protectedScope,
        additionalProtectedScopes,
      );
    }

    headers.set("Content-Type", "application/json");
    return await this.#request<TResponse>(
      url,
      { ...requestOptions, method, headers, body: JSON.stringify(body) },
      protectedScope,
      additionalProtectedScopes,
    );
  }

  #headers(url: URL, input?: HeadersInit): Headers {
    const headers = new Headers(input);
    headers.set("Accept", "application/json");
    if (
      url.origin === this.#baseUrl.origin &&
      url.pathname.startsWith(this.#baseUrl.pathname)
    ) {
      this.#bearerToken?.apply(headers);
    }
    return headers;
  }

  async #request<TResponse>(
    url: URL,
    init: RequestInit,
    protectedScope?: string,
    additionalProtectedScopes: readonly string[] = [],
  ): Promise<TResponse> {
    const protectedScopes = [...new Set([
      ...(protectedScope === undefined ? [] : [protectedScope]),
      ...additionalProtectedScopes,
    ])];
    if (protectedScopes.some(
      (scope) => this.#isProtectedScopeAvailable?.(scope) === false,
    )) {
      throw new ApiProblem(403);
    }

    const abortController = protectedScopes.length === 0 ? undefined : new AbortController();
    const originalSignal = init.signal ?? undefined;
    const forwardAbort = (): void => abortController?.abort(originalSignal?.reason);
    if (abortController !== undefined && originalSignal !== undefined) {
      if (originalSignal.aborted) {
        forwardAbort();
      } else {
        originalSignal.addEventListener("abort", forwardAbort, { once: true });
      }
    }
    const unregisterOperations = abortController === undefined
      ? []
      : protectedScopes.map((scope) => (
        this.#registerProtectedOperation?.(scope, () => abortController.abort())
      ));

    try {
      return await requestJson<TResponse>(
        url,
        abortController === undefined ? init : { ...init, signal: abortController.signal },
        this.#fetch,
      );
    } catch (error: unknown) {
      if (error instanceof ApiProblem && error.status === 401) {
        this.#bearerToken?.clear();
        await this.#onUnauthorized?.();
      } else if (error instanceof ApiProblem && error.status === 403) {
        await this.#onForbidden?.(protectedScope);
      }
      throw error;
    } finally {
      originalSignal?.removeEventListener("abort", forwardAbort);
      for (const unregisterOperation of unregisterOperations) unregisterOperation?.();
    }
  }

  #resolve(path: string | URL): URL {
    if (path instanceof URL) {
      return path;
    }
    return new URL(path.replace(/^\/+/, ""), this.#baseUrl);
  }
}

export async function requestJson<T>(
  input: RequestInfo | URL,
  init?: RequestInit,
  fetchImplementation: FetchImplementation = fetch,
): Promise<T> {
  const response = await fetchImplementation(input, init);
  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    let body: unknown;
    if (contentType.includes("application/problem+json")) {
      body = await response.json().catch(() => undefined);
    }
    const problem = isProblemDetails(body) ? body : undefined;
    throw new ApiProblem(response.status, problem);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const body = await response.text();
  if (body.length === 0) {
    return undefined as T;
  }
  return JSON.parse(body) as T;
}
